import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  Box,
  Icon,
  type NestedListRowData,
  NestedListRowGroup,
} from '@saviynt/design-system';

import { CF_SEC_MED } from '../../../../../shared/constants';
import { type TrackedFunctionData } from '../../../../../shared/types';
import {
  type TrackedViolationFunctions,
  type ViolationAssignedEntitlements,
} from '../../../../Details/data-layer/detailsTypes';
import { useConflictApiEndpoints } from '../../../../Details/hooks';
import { type FunctionAnalysisState } from '../../../data-layer/violationsTabTypes';

import {
  RemediateFunctionAfterContent,
  RemediateFunctionAsgEntitlement,
  RemediateFuntionCenterContent,
  RemediateScreenError,
  RemediateScreenLoader,
  RemediateSingletonFuntionCenterContent,
} from './RemediateScreensShared';
import { type ActiveScreenType } from './ViolationsRemediateAction';

interface RemediationFunctionScreenProps {
  sodKey: string;
  activeScreen: ActiveScreenType;
  functions?: TrackedFunctionData[];
  setIsSectionError: (state: boolean) => void;
  setFunctionAnalysis: (analysis: FunctionAnalysisState | null) => void;
  goNext?: (fnKeys: string[]) => void;
}

/**
 * RemediationFunctionScreen
 */
function RemediationFunctionScreen({
  sodKey,
  activeScreen,
  functions,
  setIsSectionError,
  setFunctionAnalysis,
  goNext,
}: RemediationFunctionScreenProps) {
  const sreeenRef = useRef<HTMLDivElement | null>(null);

  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsData, setRowsData] = useState<NestedListRowData[]>([]);
  const [analysis, setAnalysis] = useState<FunctionAnalysisState | null>(null);

  const setFunctions = useCallback(() => {
    if (functions) {
      setRowsData(
        functions.map((fn) => ({
          id: fn.functionKey,
          data: {
            ...fn,
            tracker: {
              level: 0,
              childrenCount: 0,
              type: 'Function',
              id: fn.functionKey,
            },
          },
          isRowConnectTransitionEnabled: false,
          isCheckboxDisabled: true,
          isNestedRowExpanded: true,
          centerContent: (
            <RemediateFuntionCenterContent
              order={fn.key}
              fnName={fn.functionName}
              entCount={0}
            />
          ),
        }))
      );
    }
  }, [functions]);

  // API calls
  const {
    handleFetchAssignedEntitlements,
    handleFetchFunctionAnalysis,
    states: { isFunctionAnalysisError },
  } = useConflictApiEndpoints([], null);

  const triggerFetchAssignedEntitlements = useCallback(() => {
    setIsError(false);
    setIsLoading(true);
    setIsSectionError(false);

    if (functions) {
      Promise.allSettled([
        ...functions.map((fn) =>
          handleFetchAssignedEntitlements(sodKey, fn.functionKey, null, {
            isOnExpand: true,
            maxCount: 5,
          })
        ),
        handleFetchFunctionAnalysis(sodKey),
      ])
        .then((results) => {
          const isApiError = results.some(
            (result) => result.status === 'rejected'
          );

          if (isApiError) {
            throw Error('Failed response');
          }

          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              return;
            }

            if (index === functions.length) {
              const { data } = result.value as {
                data: FunctionAnalysisState;
              };

              setAnalysis(data);

              return;
            }

            const { data: response, currentAfterKey } = result.value as {
              data: ViolationAssignedEntitlements[];
              currentAfterKey: string | null;
            };
            const isValidNextCall = Boolean(
              typeof currentAfterKey === 'string'
            );

            setRowsData((prev) =>
              prev.map((fn) => {
                const myFn = functions[index];

                if (fn.id === myFn.functionKey) {
                  const { data: typedData } = fn as {
                    data: TrackedViolationFunctions;
                  };
                  const {
                    childrenCount: currentChildrenCount,
                    level: currentLevel,
                  } = typedData.tracker;
                  const newChildrenCount =
                    currentChildrenCount + response.length;

                  return {
                    ...fn,
                    data: {
                      ...typedData,
                      tracker: {
                        ...typedData.tracker,
                        childrenCount: newChildrenCount,
                      },
                    },
                    afterContent: (
                      <RemediateFunctionAfterContent
                        onClick={() => {
                          if (goNext) {
                            goNext([myFn.functionKey]);
                          }
                        }}
                        isValidNextCall={isValidNextCall}
                      />
                    ),
                    centerContent: (
                      <RemediateFuntionCenterContent
                        order={myFn.key}
                        fnName={myFn.functionName}
                        entCount={newChildrenCount}
                      />
                    ),

                    handleExpandToggler: undefined,
                    childRows: [
                      ...response.map((ent) => {
                        const uniqueId = Number(
                          `${fn.id}${ent.entitlementKey}`
                        );

                        return {
                          id: uniqueId,
                          data: {
                            id: uniqueId,
                            tracker: {
                              id: Number(ent.entitlementKey),
                              level: currentLevel + 1,
                              childrenCount: 0,
                              type: 'Assigned Entilement',
                            },
                          },
                          isCheckboxDisabled: true,
                          leadingComp: (
                            <Icon kind='Entitlement' color={CF_SEC_MED} />
                          ),
                          centerContent: (
                            <RemediateFunctionAsgEntitlement
                              entTypeName={ent.entitlementTypeName}
                              displayName={ent.entitlementDisplayName}
                              entValue={ent.entitlementValue}
                            />
                          ),
                          childRows: undefined,
                        };
                      }),
                    ],
                  };
                }

                return fn;
              })
            );
          });
        })
        .catch((error) => {
          setIsError(true);
          setIsSectionError(true);
          setFunctions();

          Logger.log('error', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    sodKey,
    functions,
    goNext,
    setIsSectionError,
    setFunctions,
    handleFetchFunctionAnalysis,
    handleFetchAssignedEntitlements,
  ]);

  // Reset
  useEffect(() => {
    setFunctions();

    sreeenRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [setFunctions]);

  useEffect(() => {
    setFunctionAnalysis(analysis);
  }, [analysis, setFunctionAnalysis]);

  useEffect(() => {
    if (activeScreen === 1) {
      const timer = setTimeout(() => triggerFetchAssignedEntitlements(), 20);

      return () => clearTimeout(timer);
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should only depend on `activeScreen`
  }, [activeScreen]);

  // Body
  const renderBody = useCallback(() => {
    if (isError) {
      return (
        <RemediateScreenError
          isAccessDenied={isFunctionAnalysisError}
          onRefresh={() => triggerFetchAssignedEntitlements()}
        />
      );
    }

    if (isLoading || !functions || analysis === null) {
      return <RemediateScreenLoader />;
    }

    if (
      analysis.sameEntitlementsInAllFunctions &&
      analysis.functionEntitlementCount.length > 1
    ) {
      const singleRecord = rowsData[0];

      singleRecord.centerContent = (
        <RemediateSingletonFuntionCenterContent
          fnNames={functions.map((fn) => fn.functionName)}
          entCount={analysis.functionEntitlementCount[0].entitlementCount}
        />
      );

      return (
        <div ref={sreeenRef} className='RemediationFunctionScreen-listWrapper'>
          <NestedListRowGroup
            rowsData={[singleRecord]}
            setRowsData={setRowsData}
            selectedRowData={{}}
            setSelectedRowData={() => {}}
            tallestRowHeight={5.5}
            afterContentHeight={2.5}
          />
        </div>
      );
    }

    return (
      <div ref={sreeenRef} className='RemediationFunctionScreen-listWrapper'>
        <NestedListRowGroup
          rowsData={rowsData}
          setRowsData={setRowsData}
          selectedRowData={{}}
          setSelectedRowData={() => {}}
          tallestRowHeight={5.5}
          afterContentHeight={2.5}
        />
      </div>
    );
  }, [
    functions,
    analysis,
    isLoading,
    isError,
    rowsData,
    isFunctionAnalysisError,
    triggerFetchAssignedEntitlements,
  ]);

  return (
    <Box tag='div' className='RemediationFunctionScreen'>
      {renderBody()}
    </Box>
  );
}

RemediationFunctionScreen.defaultProps = {
  functions: null,
  goNext: () => {},
};

export default memo(RemediationFunctionScreen);
